// theme.js
import { createTheme } from "@mui/material/styles";
import { red, amber, teal, grey, blue } from "@mui/material/colors";

// Common status colors (error, warning, info, success)
const baseStatusColors = (mode) => ({
  error: {
    main: mode === "light" ? red[700] : red[500],
    light: mode === "light" ? red[500] : red[300],
    dark: mode === "light" ? red[900] : red[700],
    contrastText: "#fff",
  },
  warning: {
    main: mode === "light" ? amber[700] : amber[400],
    light: mode === "light" ? amber[500] : amber[300],
    dark: mode === "light" ? amber[900] : amber[600],
    contrastText: "#fff",
  },
  info: {
    main: mode === "light" ? teal[500] : teal[300],
    light: mode === "light" ? teal[300] : teal[200],
    dark: mode === "light" ? teal[700] : teal[500],
    contrastText: "#fff",
  },
  success: {
    main: mode === "light" ? "#43a047" : "#66bb6a",
    light: mode === "light" ? "#66bb6a" : "#81c784",
    dark: mode === "light" ? "#388e3c" : "#388e3c",
    contrastText: "#fff",
  },
});

// Semantic colors for checklist components
const checklistColors = (mode, primaryMain, secondaryMain) => {
  const isLight = mode === "light";

  return {
    checklist: {
      // Surface colors for different container types
      surface: {
        primary: isLight ? "#fafafa" : "#1a1a1a",
        elevated: isLight ? "#ffffff" : "#212121",
        subtle: isLight ? "#f8f9fa" : "#161616",
      },
      // Border colors for different element states
      border: {
        primary: isLight ? `${primaryMain}20` : `${primaryMain}30`,
        secondary: isLight ? `${secondaryMain}20` : `${secondaryMain}30`,
        subtle: isLight ? grey[200] : grey[700],
        focus: isLight ? `${primaryMain}40` : `${primaryMain}50`,
      },
      // Section-specific colors
      section: {
        background: isLight ? blue[50] : blue[900] + "10",
        border: isLight ? blue[200] : blue[600],
        text: isLight ? blue[800] : blue[300],
        accent: isLight ? blue[100] : blue[800],
      },
      // Question-specific colors
      question: {
        background: isLight ? teal[50] : teal[900] + "10",
        border: isLight ? teal[200] : teal[600],
        text: isLight ? teal[800] : teal[300],
        accent: isLight ? teal[100] : teal[800],
      },
      // Badge and indicator colors
      badge: {
        section: isLight ? blue[600] : blue[400],
        question: isLight ? teal[600] : teal[400],
        counter: isLight ? grey[600] : grey[400],
        background: isLight ? grey[100] : grey[800],
      },
      // Interactive element colors
      interactive: {
        hover: isLight ? grey[50] : grey[900],
        selected: isLight ? `${primaryMain}08` : `${primaryMain}12`,
        disabled: isLight ? grey[300] : grey[600],
      },
      // Empty state colors
      empty: {
        background: isLight ? grey[50] : grey[900],
        border: isLight ? grey[300] : grey[600],
        icon: isLight ? grey[400] : grey[500],
        text: isLight ? grey[600] : grey[400],
      },
    },
  };
};

// Dashboard-specific colors for charts and analytics
const dashboardColors = (mode, primaryMain) => {
  const isLight = mode === "light";

  return {
    dashboard: {
      // Background variations
      background: {
        main: isLight ? "#f8fafc" : "#0f1419",
        elevated: isLight ? "#ffffff" : "#1a1f2e",
        subtle: isLight ? "#f1f5f9" : "#151b26",
        card: isLight ? "#ffffff" : "#1e293b",
      },
      // Chart colors that work in both themes
      chart: {
        colors: isLight
          ? [
              "#3b82f6", // blue
              "#10b981", // emerald
              "#f59e0b", // amber
              "#ef4444", // red
              "#8b5cf6", // violet
              "#06b6d4", // cyan
              "#f97316", // orange
              "#84cc16", // lime
              "#ec4899", // pink
              "#6366f1", // indigo
              "#14b8a6", // teal
              "#f43f5e", // rose
              "#a855f7", // purple
              "#22d3ee", // cyan-light
              "#facc15", // yellow
            ]
          : [
              "#60a5fa", // blue-400
              "#34d399", // emerald-400
              "#fbbf24", // amber-400
              "#f87171", // red-400
              "#a78bfa", // violet-400
              "#22d3ee", // cyan-400
              "#fb923c", // orange-400
              "#a3e635", // lime-400
              "#f472b6", // pink-400
              "#818cf8", // indigo-400
              "#2dd4bf", // teal-400
              "#fb7185", // rose-400
              "#c084fc", // purple-400
              "#67e8f9", // cyan-300
              "#fde047", // yellow-400
            ],
        primary: primaryMain,
        secondary: isLight ? grey[600] : grey[400],
        accent: isLight ? blue[500] : blue[400],
      },
      // Gradient backgrounds
      gradient: {
        primary: isLight
          ? `linear-gradient(135deg, ${primaryMain}08 0%, ${primaryMain}03 100%)`
          : `linear-gradient(135deg, ${primaryMain}15 0%, ${primaryMain}08 100%)`,
        card: isLight
          ? `linear-gradient(135deg, ${grey[50]} 0%, ${grey[25] || "#fefefe"} 100%)`
          : `linear-gradient(135deg, ${grey[800]} 0%, ${grey[900]} 100%)`,
        modal: isLight
          ? `linear-gradient(135deg, ${primaryMain}05 0%, ${primaryMain}02 100%)`
          : `linear-gradient(135deg, ${primaryMain}12 0%, ${primaryMain}06 100%)`,
      },
      // Shadows for different themes
      shadow: {
        card: isLight
          ? "0 4px 16px rgba(0, 0, 0, 0.08)"
          : "0 4px 16px rgba(0, 0, 0, 0.3)",
        modal: isLight
          ? "0 24px 48px rgba(0, 0, 0, 0.12)"
          : "0 24px 48px rgba(0, 0, 0, 0.5)",
        hover: isLight
          ? "0 8px 32px rgba(0, 0, 0, 0.12)"
          : "0 8px 32px rgba(0, 0, 0, 0.4)",
      },
      // Interactive states
      interaction: {
        hover: isLight ? `${primaryMain}08` : `${primaryMain}15`,
        active: isLight ? `${primaryMain}12` : `${primaryMain}20`,
        focus: isLight ? `${primaryMain}20` : `${primaryMain}30`,
      },
      // Text colors for various contexts
      text: {
        primary: isLight ? grey[900] : grey[100],
        secondary: isLight ? grey[600] : grey[400],
        muted: isLight ? grey[500] : grey[500],
        inverse: isLight ? grey[100] : grey[900],
      },
    },
  };
};

// Four named palettes, each with explicit contrastText values
const palettes = {
  orange: {
    light: {
      mode: "light",
      primary: {
        main: "#d85404",
        contrastText: "#ffffff",
      },
      secondary: {
        main: "#2196f3",
        contrastText: "hsl(20,14.3%,4.1%)",
      },
      background: {
        default: "#ffffff",
        paper: "#ffffff",
      },
      text: {
        primary: "hsl(20,14.3%,4.1%)", // nearly black on white
        secondary: "hsl(25,5.3%,44.7%)",
      },
      ...baseStatusColors("light"),
      ...checklistColors("light", "#d85404", "#2196f3"),
      ...dashboardColors("light", "#d85404"),
    },
    dark: {
      mode: "dark",
      primary: {
        main: "#d85404",
        contrastText: "#ffffff",
      },
      secondary: {
        main: "#2196f3",
        contrastText: "hsl(60,9.1%,97.8%)",
      },
      background: {
        default: "hsl(20,14.3%,4.1%)",
        paper: "hsl(20,14.3%,4.1%)",
      },
      text: {
        primary: "hsl(60,9.1%,97.8%)", // off‐white on dark
        secondary: "hsl(24,5.4%,63.9%)",
      },
      ...baseStatusColors("dark"),
      ...checklistColors("dark", "#d85404", "#2196f3"),
      ...dashboardColors("dark", "#d85404"),
    },
  },
  violet: {
    light: {
      mode: "light",
      primary: {
        main: "#6814d4",
        contrastText: "#ffffff",
      },
      secondary: {
        main: "#f97315",
        contrastText: "hsl(224,71.4%,4.1%)",
      },
      background: {
        default: "#ffffff",
        paper: "#ffffff",
      },
      text: {
        primary: "hsl(224,71.4%,4.1%)", // near‐black
        secondary: "hsl(220,8.9%,46.1%)",
      },
      ...baseStatusColors("light"),
      ...checklistColors("light", "#6814d4", "#f97315"),
      ...dashboardColors("light", "#6814d4"),
    },
    dark: {
      mode: "dark",
      primary: {
        main: "hsl(264, 85%, 65%)",
        contrastText: "#ffffff",
      },
      secondary: {
        main: "#f97315",
        contrastText: "hsl(210,20%,98%)",
      },
      background: {
        default: "hsl(224,71.4%,4.1%)",
        paper: "hsl(224,71.4%,4.1%)",
      },
      text: {
        primary: "hsl(210,20%,98%)", // off‐white
        secondary: "hsl(217.9,10.6%,64.9%)",
      },
      ...baseStatusColors("dark"),
      ...checklistColors("dark", "hsl(264, 85%, 65%)", "#f97315"),
      ...dashboardColors("dark", "hsl(264, 85%, 65%)"),
    },
  },
  green: {
    light: {
      mode: "light",
      primary: {
        main: "hsl(142.1,76.2%,36.3%)",
        contrastText: "#ffffff",
      },
      secondary: {
        main: "#f97315",
        contrastText: "hsl(240,10%,3.9%)",
      },
      background: {
        default: "#ffffff",
        paper: "#ffffff",
      },
      text: {
        primary: "hsl(240,10%,3.9%)", // dark almost black on white
        secondary: "hsl(240,3.8%,46.1%)",
      },
      ...baseStatusColors("light"),
      ...checklistColors("light", "hsl(142.1,76.2%,36.3%)", "#f97315"),
      ...dashboardColors("light", "hsl(142.1,76.2%,36.3%)"),
    },
    dark: {
      mode: "dark",
      primary: {
        main: "#289c4c",
        contrastText: "#ffffff",
      },
      secondary: {
        main: "#f97315",
        contrastText: "hsl(0,0%,95%)",
      },
      background: {
        default: "hsl(20,14.3%,4.1%)",
        paper: "hsl(24,9.8%,10%)",
      },
      text: {
        primary: "hsl(0,0%,95%)", // off‐white on dark
        secondary: "hsl(240,5%,64.9%)",
      },
      ...baseStatusColors("dark"),
      ...checklistColors("dark", "#289c4c", "#f97315"),
      ...dashboardColors("dark", "#289c4c"),
    },
  },
  darkBlue: {
    light: {
      mode: "light",
      primary: {
        main: "#002752",
        contrastText: "#ffffff",
      },
      secondary: {
        main: "#f97315",
        contrastText: "hsl(240,10%,3.9%)",
      },
      background: {
        default: "#f5f5f5",
        paper: "#ffffff",
      },
      text: {
        primary: grey[900], // dark grey
        secondary: grey[700],
      },
      ...baseStatusColors("light"),
      ...checklistColors("light", "#002752", "#f97315"),
      ...dashboardColors("light", "#002752"),
    },
    dark: {
      mode: "dark",
      primary: {
        main: "hsl(210, 80%, 62%)",
        contrastText: "#ffffff",
      },
      secondary: {
        main: "#fb8c00",
        contrastText: grey[400],
      },
      background: {
        default: "#121212",
        paper: "#1e1e1e",
      },
      text: {
        primary: "#ffffff",
        secondary: grey[400],
      },
      ...baseStatusColors("dark"),
      ...checklistColors("dark", "hsl(210, 80%, 62%)", "#fb8c00"),
      ...dashboardColors("dark", "hsl(210, 80%, 62%)"),
    },
  },
};

export const createAppTheme = (paletteName = "orange", mode = "light") => {
  const chosen = palettes[paletteName]?.[mode] ?? palettes.orange[mode];

  return createTheme({
    palette: {
      mode: chosen.mode,
      primary: {
        main: chosen.primary.main,
        contrastText: chosen.primary.contrastText,
      },
      secondary: {
        main: chosen.secondary.main,
        contrastText: chosen.secondary.contrastText,
      },
      background: {
        default: chosen.background.default,
        paper: chosen.background.paper,
      },
      text: {
        primary: chosen.text.primary,
        secondary: chosen.text.secondary,
      },
      error: {
        main: chosen.error.main,
        light: chosen.error.light,
        dark: chosen.error.dark,
        contrastText: chosen.error.contrastText,
      },
      warning: {
        main: chosen.warning.main,
        light: chosen.warning.light,
        dark: chosen.warning.dark,
        contrastText: chosen.warning.contrastText,
      },
      info: {
        main: chosen.info.main,
        light: chosen.info.light,
        dark: chosen.info.dark,
        contrastText: chosen.info.contrastText,
      },
      success: {
        main: chosen.success.main,
        light: chosen.success.light,
        dark: chosen.success.dark,
        contrastText: chosen.success.contrastText,
      },
      // Checklist semantic colors
      checklist: chosen.checklist,
      // Dashboard semantic colors
      dashboard: chosen.dashboard,
    },
    shape: {
      borderRadius: 8,
    },
  });
};
